import {HelmetProvider} from '@dr.pogodin/react-helmet';
import {useFlagsmith} from 'feature-flags';
import {ComponentsProvider} from 'platform/components';
import {StyleSheetManager} from 'styled-components';

import {useState, useEffect} from 'react';
import {Provider} from 'react-redux';
import {createBrowserRouter, RouterProvider} from 'react-router-dom';

import {PersistGate} from 'features/rtkq-persist';

import {FilterLayout} from './components/FilterLayout/FilterLayout';
import {IndexRedirect} from './components/IndexRedirect/IndexRedirect';
import {Layout} from './components/Layout/Layout';
import {PriceReportRouteGuard} from './components/PriceReportRouteGuard/PriceReportRouteGuard';
import {GlobalStyles} from './GlobalStyles';
import {FALLBACK_LANGUAGE} from './i18n/fallbackLanguage';
import i18n from './i18n/i18n';
import {AdvertisementPage} from './pages/AdvertisementPage/AdvertisementPage';
import {CreatePage} from './pages/Create/CreatePage';
import {DrillDownPage} from './pages/DrillDown/DrillDownPage';
import {ErrorPage} from './pages/ErrorPage/ErrorPage';
import {OverviewPage} from './pages/Overview/OverviewPage';
import {ReasoningPage} from './pages/ReasoningPage/ReasoningPage';
import {StatisticsPage} from './pages/StatisticsPage/StatisticsPage';
import {reducers, store} from './store/store';
import {theme} from './theme';
import {
  advertisementRoute,
  createRoute,
  drillDownRoute,
  overviewRoute,
  reasoningRoute,
  reportRoute,
  statisticsRoute,
} from './utils/routing';

/*
  Router defined outside component to avoid re-creation on state changes.
  Providers wrap RouterProvider (not inside router elements) to ensure they properly
  update on language changes, as React Router doesn't re-render static element trees.
*/
const router = createBrowserRouter([
  {
    path: '/',
    element: <ErrorPage isNotFound data-testid="errorPage" />,
    errorElement: <ErrorPage data-testid="errorPage" />,
  },
  {
    path: reportRoute,
    element: <PriceReportRouteGuard />,
    errorElement: <ErrorPage data-testid="errorPage" />,
    children: [
      {
        element: <Layout />,
        children: [
          {
            index: true,
            element: <IndexRedirect />,
          },
          {
            path: createRoute,
            element: <CreatePage data-testid="createPage" />,
          },
          {
            path: overviewRoute,
            element: <OverviewPage data-testid="overviewPage" />,
          },
          {
            path: drillDownRoute,
            element: <DrillDownPage data-testid="drilldownPage" />,
          },
          {
            path: reasoningRoute,
            element: <ReasoningPage data-testid="reasoningPage" />,
          },
          {
            element: <FilterLayout />,
            children: [
              {
                path: statisticsRoute,
                element: <StatisticsPage data-testid="statisticsPage" />,
              },
              {
                path: advertisementRoute,
                element: <AdvertisementPage data-testid="advertisementPage" />,
              },
            ],
          },
          {
            path: '*',
            element: <IndexRedirect />,
          },
        ],
      },
    ],
  },
]);

export function App() {
  const [language, setLanguage] = useState(i18n.resolvedLanguage || FALLBACK_LANGUAGE);
  const {isFlagSmithInitialized} = useFlagsmith({
    apiUrl: `${process.env.FLAGSMITH_URL}/api/v1/`,
    environmentID: process.env.FLAGSMITH_ENV!,
  });

  useEffect(() => {
    const handleLanguageChange = (lang: string) => {
      setLanguage(lang);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  if (!isFlagSmithInitialized) {
    return null;
  }

  return (
    <Provider store={store}>
      <PersistGate reducers={reducers}>
        <StyleSheetManager enableVendorPrefixes>
          <ComponentsProvider theme={theme} t={i18n.t} language={language}>
            <HelmetProvider>
              <RouterProvider router={router} />
              <GlobalStyles />
            </HelmetProvider>
          </ComponentsProvider>
        </StyleSheetManager>
      </PersistGate>
    </Provider>
  );
}
